import {Component, EventEmitter, Input, OnInit, Output, QueryList, SimpleChanges, ViewChildren, inject} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { AddressComponent } from '../address/address.component';
import { IApplicantInformation } from 'app/models/licenses/applicant-information';
import { ApplicantInformationService } from 'app/services/license/applicant-information.service';
import { AuthService } from '@shared/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { HemoTechChangeComponent, IHemoTechChangeDialog } from 'app/components/license/hemo-tech/hemo-tech-change/hemo-tech-change.component';

@Component({
  selector: 'applicant-information',
  templateUrl: './applicant-information.component.html',
  styleUrls: ['./applicant-information.component.scss']
})

export class ApplicantInformationComponent implements OnInit {
  @ViewChildren(AddressComponent) addresses!: QueryList<AddressComponent>;
  @Input() licenseMasterId: number = 0;
  @Input() licenseId: number = 0;
  @Input() licenseGuid: string = '';
  @Input() isChange: boolean = false;
  @Input() isLocked: boolean = false;
  @Input() isRenewal: boolean = false;
  @Output() formValidityChange = new EventEmitter<boolean>();

  private lastLoadedLicenseMasterId = 0;
  public includeArchived: boolean = false;
  public applicationEntityType: string = 'ApplicantInformation';
  public applicantId: number = 0;
  public fullSSN: string = '';
  public maskedSSN: string = '';
  public ssnInvalid: boolean = false;
  public ssnVisible: boolean = false;
  public showSsn: boolean = false;
  public hundredYearsAgo: Date = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
  public eighteenYearsAgo: Date = new Date(new Date().setFullYear(new Date().getFullYear() - 18));
  public applicantInformation: IApplicantInformation = {
    id: 0,
    licenseMasterId: 0,
    firstName: '',
    lastName: '',
    middleName: '',
    phone: '',
    email: '',
    ssn: '',
    dateOfBirth: '',
    priorName: '',
    gender: ''
  }

  public applicantInfoForm = new FormGroup(
    {
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      middleName: new FormControl(''),
      phone: new FormControl('', [
        Validators.required,
        Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)
      ]),
      ssn: new FormControl('', [
        Validators.required,
        this.ssnValidator.bind(this)
      ]),
      dateOfBirth: new FormControl('', Validators.required),
      email: new FormControl('', [
        Validators.required,
        Validators.email
      ]),
      gender: new FormControl('', Validators.required),
      priorName: new FormControl('')
    }
  );

  public sharedService = inject(SharedService);
  public applicantInfoService = inject(ApplicantInformationService);
  public authService = inject(AuthService);
  public dialog = inject(MatDialog);

  ngOnInit(): void {
    if (this.isLocked)
      this.applicantInfoForm.disable();
    else
      this.applicantInfoForm.enable();

    this.loadApplicantInfoIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isChange) {
      return;
    }

    if (changes['isLocked']) {
      if (this.isLocked) this.applicantInfoForm.disable();
      else this.applicantInfoForm.enable();
      this.checkFormValidity();
    }
  }

  private loadApplicantInfoIfNeeded(): void {
    if (this.licenseMasterId > 0 && this.licenseMasterId !== this.lastLoadedLicenseMasterId) {
      this.lastLoadedLicenseMasterId = this.licenseMasterId;
      this.getApplicantInformation();
    }
  }

  saveApplicantInfo(): void {
    this.updateApplicationInformation();
    this.applicantInfoService.saveApplicantInformation(this.applicantInformation).subscribe({
      error: e => {
        console.error('Error saving applicant information:', e);
        this.sharedService.toastr.error('Error saving applicant information');
      }
    });
  }

  getApplicantInformation(): void {
    this.applicantInfoService.getApplicantInformation(this.licenseMasterId).subscribe({
      next: response => {
          this.applicantInformation = response;
          this.applicantId = this.applicantInformation.id;
          this.updateApplicantInformationForm(this.applicantInformation);
      },
      error: e => {
        console.error('Error fetching applicant information:', e);
        this.sharedService.toastr.error('Error fetching applicant information');
      },
      complete: () => {
        this.updateApplicantInformationForm(this.applicantInformation);
      }
    });
  }

  getEmptyApplicantInformation(): IApplicantInformation {
    return {
      id: 0,
      licenseMasterId: 0,
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      ssn: '',
      middleName: '',
      gender: '',
      dateOfBirth: '',
      priorName: ''
    }
  }

  updateApplicantInformationForm(applicantInfo: IApplicantInformation): void {
    this.applicantInfoForm.reset({
      firstName: applicantInfo.firstName,
      lastName: applicantInfo.lastName,
      phone: applicantInfo.phone,
      email: applicantInfo.email,
      ssn: applicantInfo.ssn,
      dateOfBirth: applicantInfo.dateOfBirth,
      middleName: applicantInfo.middleName,
      gender: applicantInfo.gender,
      priorName: applicantInfo.priorName
    });

    if(this.isRenewal){
      this.applicantInfoForm.get('firstName')?.disable();
      this.applicantInfoForm.get('lastName')?.disable();
      this.applicantInfoForm.get('middleName')?.disable();
      this.applicantInfoForm.get('dateOfBirth')?.disable();
      this.applicantInfoForm.get('ssn')?.disable();
    }

    this.applicantInfoForm.updateValueAndValidity();
    this.checkFormValidity();
  }

  changeName(): void {
    const dialogData: IHemoTechChangeDialog = {
      licenseId: this.licenseId,
      currentName: `${this.applicantInformation.firstName} ${this.applicantInformation.middleName} ${this.applicantInformation.lastName}`
    };
    this.dialog.open(HemoTechChangeComponent, {
      data: dialogData,
      role: 'dialog',
      ariaLabel: "Name Change Request Dialog",
      minWidth: !this.sharedService.mobile ? '800px' : '',
      maxWidth: !this.sharedService.mobile ? '1000px' : '300px',

    });
  }

  toggleSsn(): void {
    this.showSsn = !this.showSsn;
  }

  private checkFormValidity(): void {
    const isValid = this.applicantInfoForm.valid;
    this.formValidityChange.emit(isValid);
  }

  private ssnValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }

    // Remove dashes and check if we have 9 digits
    const digitsOnly = control.value.replace(/-/g, '');

    if (digitsOnly.length < 9) {
      return { 'incomplete': true };
    }

    if (!/^\d{9}$/.test(digitsOnly)) {
      return { 'invalid': true };
    }

    return null;
  }

  updateApplicationInformation(): void {
    let form = this.applicantInfoForm.getRawValue();
    this.applicantInformation.firstName = form.firstName ?? '';
    this.applicantInformation.lastName = form.lastName ?? '';
    this.applicantInformation.phone = form.phone ?? '';
    this.applicantInformation.middleName = form.middleName ?? '';
    this.applicantInformation.ssn = form.ssn ?? '';
    this.applicantInformation.dateOfBirth = form.dateOfBirth && form.dateOfBirth !== '' ? form.dateOfBirth : '';
    this.applicantInformation.email = form.email ?? '';
    this.applicantInformation.gender = form.gender ?? '';
    this.applicantInformation.priorName = form.priorName ?? '';
    this.applicantInformation.licenseMasterId = this.licenseMasterId;
  }

  public get email(): string {
    return this.authService.email;
  }

  validateApplicantInformation(): string[] {
    let errors: string[] = [];
    if (this.applicantInfoForm.invalid) {
      errors.push('Application Information is incomplete');
    }
    return errors;
  }

  validateAddresses(): string[] {
    let errors: string[] = [];
    if (this.addresses.length === 0) {
      errors.push('Please add at least one address');
    }
    return errors;
  }
}
